import { __assign, __rest } from "tslib";
import clsx from 'clsx';
import React, { useCallback, useImperativeHandle, useLayoutEffect, useState, useRef } from 'react';
import { getBaseProps } from '../internal/base-component';
import { useControllable } from '../internal/hooks/use-controllable';
import { useMobile } from '../internal/hooks/use-mobile';
import { fireNonCancelableEvent } from '../internal/events';
import { applyDefaults } from './defaults';
import { Drawer } from './drawer';
import { Notifications } from './notifications';
import { MobileToolbar } from './mobile-toolbar';
import { useObservedElement } from './utils/use-observed-element';
import styles from './styles.css.js';
import { findUpUntil } from '../internal/utils/dom';
import { AppLayoutDomContext } from '../internal/context/app-layout-context';
import { useContainerQuery } from '../internal/hooks/container-queries';
import { applyDisplayName } from '../internal/utils/apply-display-name';
import { SplitPanelContext } from '../internal/context/split-panel-context';
import { getSplitPanelDefaultSize } from '../split-panel/utils/get-default-size';
import useBaseComponent from '../internal/hooks/use-base-component';
var MAIN_PANEL_MIN_HEIGHT = 250; // approximate height of breadcrumb, table header, and the first table row
var AppLayout = React.forwardRef(function (_a, ref) {
    var _b, _c;
    var navigation = _a.navigation, _d = _a.navigationWidth, navigationWidth = _d === void 0 ? 280 : _d, navigationHide = _a.navigationHide, controlledNavigationOpen = _a.navigationOpen, tools = _a.tools, _e = _a.toolsWidth, toolsWidth = _e === void 0 ? 290 : _e, toolsHide = _a.toolsHide, controlledToolsOpen = _a.toolsOpen, breadcrumbs = _a.breadcrumbs, notifications = _a.notifications, stickyNotifications = _a.stickyNotifications, content = _a.content, _f = _a.contentType, contentType = _f === void 0 ? 'default' : _f, disableContentPaddings = _a.disableContentPaddings, disableBodyScroll = _a.disableBodyScroll, maxContentWidth = _a.maxContentWidth, minContentWidth = _a.minContentWidth, _g = _a.headerSelector, headerSelector = _g === void 0 ? '#b #h' : _g, _h = _a.footerSelector, footerSelector = _h === void 0 ? '#b #f' : _h, ariaLabels = _a.ariaLabels, splitPanel = _a.splitPanel, controlledSplitPanelSize = _a.splitPanelSize, controlledSplitPanelOpen = _a.splitPanelOpen, controlledSplitPanelPreferences = _a.splitPanelPreferences, onSplitPanelPreferencesChange = _a.onSplitPanelPreferencesChange, onSplitPanelResize = _a.onSplitPanelResize, onSplitPanelToggle = _a.onSplitPanelToggle, onNavigationChange = _a.onNavigationChange, onToolsChange = _a.onToolsChange, rest = __rest(_a, ["navigation", "navigationWidth", "navigationHide", "navigationOpen", "tools", "toolsWidth", "toolsHide", "toolsOpen", "breadcrumbs", "notifications", "stickyNotifications", "content", "contentType", "disableContentPaddings", "disableBodyScroll", "maxContentWidth", "minContentWidth", "headerSelector", "footerSelector", "ariaLabels", "splitPanel", "splitPanelSize", "splitPanelOpen", "splitPanelPreferences", "onSplitPanelPreferencesChange", "onSplitPanelResize", "onSplitPanelToggle", "onNavigationChange", "onToolsChange"]);
    var __internalRootRef = useBaseComponent('AppLayout').__internalRootRef;
    var baseProps = getBaseProps(rest);
    var isMobile = useMobile();
    var defaults = applyDefaults(contentType, { maxContentWidth: maxContentWidth, minContentWidth: minContentWidth });
    var _j = useControllable(controlledNavigationOpen, onNavigationChange, isMobile ? false : defaults.navigationOpen, { componentName: 'AppLayout', controlledProp: 'navigationOpen', changeHandler: 'onNavigationChange' }), navigationOpen = _j[0], setNavigationOpen = _j[1];
    var _k = useState(), activeElement = _k[0], setActiveElement = _k[1];
    var _l = useControllable(controlledToolsOpen, onToolsChange, isMobile ? false : defaults.toolsOpen, { componentName: 'AppLayout', controlledProp: 'toolsOpen', changeHandler: 'onToolsChange' }), toolsOpen = _l[0], setToolsOpen = _l[1];
    var onNavigationToggle = useCallback(function (open) {
        setNavigationOpen(open);
        fireNonCancelableEvent(onNavigationChange, { open: open });
    }, [setNavigationOpen, onNavigationChange]);
    var onToolsToggle = useCallback(function (open) {
        setToolsOpen(open);
        fireNonCancelableEvent(onToolsChange, { open: open });
    }, [setToolsOpen, onToolsChange]);
    var onNavigationClick = function (event) {
        var hasLink = findUpUntil(event.target, function (node) { return node.tagName === 'A' && !!node.href; });
        if (hasLink) {
            onNavigationToggle(false);
        }
    };
    var headerHeight = useObservedElement(headerSelector);
    var footerHeight = useObservedElement(footerSelector);
    var _m = useContainerQuery(function (rect) { return rect.height; }), notificationsHeight = _m[0], notificationsRef = _m[1];
    var _o = useContainerQuery(function (rect) { return (splitPanel ? rect.height : 0); }, [splitPanel]), splitPanelHeight = _o[0], splitPanelRef = _o[1];
    var anyPanelOpen = (navigationOpen && !navigationHide) || (toolsOpen && !toolsHide);
    var _p = useControllable(controlledSplitPanelPreferences, onSplitPanelPreferencesChange, { position: 'bottom' }, {
        componentName: 'AppLayout',
        controlledProp: 'splitPanelPreferences',
        changeHandler: 'onSplitPanelPreferencesChange'
    }), splitPanelPreferences = _p[0], setSplitPanelPreferences = _p[1];
    var splitPanelPosition = (splitPanelPreferences === null || splitPanelPreferences === void 0 ? void 0 : splitPanelPreferences.position) || 'bottom';
    var defaultSplitPanelSize = getSplitPanelDefaultSize(splitPanelPosition);
    var _q = useControllable(controlledSplitPanelSize, onSplitPanelResize, defaultSplitPanelSize, {
        componentName: 'AppLayout',
        controlledProp: 'splitPanelSize',
        changeHandler: 'onSplitPanelResize'
    }), splitPanelSize = _q[0], setSplitPanelSize = _q[1];
    var _r = useControllable(controlledSplitPanelOpen, onSplitPanelToggle, false, {
        componentName: 'AppLayout',
        controlledProp: 'splitPanelOpen',
        changeHandler: 'onSplitPanelToggle'
    }), splitPanelOpen = _r[0], setSplitPanelOpen = _r[1];
    // Allow narrow view overriding position and size
    var _s = useState((splitPanelPreferences === null || splitPanelPreferences === void 0 ? void 0 : splitPanelPreferences.position) || 'bottom'), localSplitPanelPosition = _s[0], setLocalSplitPanelPosition = _s[1];
    var _t = useState(splitPanelSize || defaultSplitPanelSize), localSplitPanelSize = _t[0], setLocalSplitPanelSize = _t[1];
    var isSideSplitPanel = !!splitPanel && localSplitPanelPosition === 'side';
    var isBottomSplitPanel = !!splitPanel && localSplitPanelPosition === 'bottom';
    var _u = useContainerQuery(function (rect) { return rect.width; }), mainPanelWidth = _u[0], mainPanelRef = _u[1];
    var mainContentRef = useRef(null);
    var legacyScrollRootRef = useRef(null);
    var onSplitPanelPreferencesSet = useCallback(function (detail) {
        setSplitPanelPreferences(detail);
        fireNonCancelableEvent(onSplitPanelPreferencesChange, detail);
    }, [setSplitPanelPreferences, onSplitPanelPreferencesChange]);
    var onSplitPanelSizeSet = useCallback(function (detail) {
        setSplitPanelSize(detail.size);
        fireNonCancelableEvent(onSplitPanelResize, detail);
    }, [setSplitPanelSize, onSplitPanelResize]);
    var onToggle = useCallback(function () {
        setSplitPanelOpen(!splitPanelOpen);
        fireNonCancelableEvent(onSplitPanelToggle, { open: !splitPanelOpen });
    }, [setSplitPanelOpen, splitPanelOpen, onSplitPanelToggle]);
    var getSplitPanelMaxWidth = useCallback(function () {
        if (!mainContentRef.current || !defaults.minContentWidth) {
            return NaN;
        }
        var width = parseInt(getComputedStyle(mainContentRef.current).width);
        var buffer = localSplitPanelPosition === 'side' ? localSplitPanelSize : 0;
        return Math.max(0, buffer + (width - defaults.minContentWidth));
    }, [defaults.minContentWidth, localSplitPanelPosition, localSplitPanelSize]);
    var getSplitPanelMaxHeight = useCallback(function () {
        if (typeof document === 'undefined') {
            return 0; // render the split panel in its minimum possible size
        }
        else if (disableBodyScroll && legacyScrollRootRef.current) {
            return legacyScrollRootRef.current.clientHeight - MAIN_PANEL_MIN_HEIGHT;
        }
        else {
            return document.documentElement.clientHeight - headerHeight - footerHeight - MAIN_PANEL_MIN_HEIGHT;
        }
    }, [disableBodyScroll, headerHeight, footerHeight]);
    var _v = useState(getSplitPanelMaxWidth), splitPanelMaxWidth = _v[0], setSplitPanelMaxWidth = _v[1];
    var _w = useState(getSplitPanelMaxHeight), splitPanelMaxHeight = _w[0], setSplitPanelMaxHeight = _w[1];
    var updateSplitPanelMaxWidth = useCallback(function () {
        var newMaxWidth = getSplitPanelMaxWidth();
        if (splitPanelMaxWidth !== newMaxWidth) {
            setSplitPanelMaxWidth(newMaxWidth);
        }
    }, [getSplitPanelMaxWidth, splitPanelMaxWidth]);
    var updateSplitPanelMaxHeight = useCallback(function () {
        var newMaxHeight = getSplitPanelMaxHeight();
        if (splitPanelMaxHeight !== newMaxHeight) {
            setSplitPanelMaxHeight(newMaxHeight);
        }
    }, [getSplitPanelMaxHeight, splitPanelMaxHeight]);
    // Update split panel max width when the navigation or tools panels are toggled
    useLayoutEffect(updateSplitPanelMaxWidth, [
        updateSplitPanelMaxWidth,
        toolsOpen,
        navigationOpen,
        toolsHide,
        navigationHide
    ]);
    // Update split panel max height after refs are set or when disableBodyScroll is changed
    useLayoutEffect(updateSplitPanelMaxHeight, [updateSplitPanelMaxHeight, disableBodyScroll]);
    // Update split panel max sizes when the window is resized
    useLayoutEffect(function () {
        if (splitPanel) {
            window.addEventListener('resize', updateSplitPanelMaxWidth);
            window.addEventListener('resize', updateSplitPanelMaxHeight);
            return function () {
                window.removeEventListener('resize', updateSplitPanelMaxWidth);
                window.removeEventListener('resize', updateSplitPanelMaxHeight);
            };
        }
    }, [updateSplitPanelMaxWidth, localSplitPanelPosition, splitPanel, updateSplitPanelMaxHeight]);
    var wrappedSplitPanel = (React.createElement(SplitPanelContext.Provider, { value: {
            topOffset: headerHeight + (localSplitPanelPosition === 'bottom' ? notificationsHeight || 0 : 0),
            bottomOffset: footerHeight,
            mainPanelWidth: mainPanelWidth || 0,
            position: localSplitPanelPosition,
            size: localSplitPanelSize,
            maxWidth: splitPanelMaxWidth,
            maxHeight: splitPanelMaxHeight,
            disableContentPaddings: disableContentPaddings,
            isOpen: splitPanelOpen,
            isMobile: isMobile,
            splitPanelRef: splitPanelRef,
            onResize: onSplitPanelSizeSet,
            onToggle: onToggle,
            onPreferencesChange: onSplitPanelPreferencesSet
        } }, splitPanel));
    useLayoutEffect(function () {
        setActiveElement(navigationOpen ? 'navigation-close' : 'navigation-toggle');
    }, [navigationOpen]);
    useLayoutEffect(function () {
        setActiveElement(toolsOpen ? 'tools-close' : 'tools-toggle');
    }, [toolsOpen]);
    useImperativeHandle(ref, function () { return ({
        openTools: function () { return onToolsToggle(true); },
        closeNavigationIfNecessary: function () {
            if (isMobile) {
                onNavigationToggle(false);
            }
        }
    }); }, [isMobile, onNavigationToggle, onToolsToggle]);
    // Force split panel to use bottom position for narrow view
    useLayoutEffect(function () {
        if (!splitPanel || typeof splitPanelSize === 'undefined') {
            return;
        }
        if (typeof splitPanelSize !== 'undefined' && splitPanelSize !== localSplitPanelSize) {
            setLocalSplitPanelSize(splitPanelSize);
        }
        if (splitPanelPreferences === null || splitPanelPreferences === void 0 ? void 0 : splitPanelPreferences.position) {
            setLocalSplitPanelPosition(splitPanelPreferences.position);
        }
        var fallbackPosition = (splitPanelPreferences === null || splitPanelPreferences === void 0 ? void 0 : splitPanelPreferences.position) || 'bottom';
        // Overwrite values for both controlled and uncontrolled cases
        var fitsInScreen = !isMobile && splitPanelMaxWidth > (defaults.minContentWidth || 0);
        if (!fitsInScreen && (splitPanelPreferences === null || splitPanelPreferences === void 0 ? void 0 : splitPanelPreferences.position) !== 'bottom') {
            var size = splitPanelSize;
            setSplitPanelSize(size);
            setLocalSplitPanelSize(size);
            setLocalSplitPanelPosition('bottom');
        }
        else if (fitsInScreen && localSplitPanelPosition !== fallbackPosition) {
            var size = splitPanelSize;
            setSplitPanelSize(size);
            setLocalSplitPanelSize(size);
            setLocalSplitPanelPosition(fallbackPosition);
        }
    }, [
        splitPanel,
        isMobile,
        splitPanelMaxWidth,
        defaults.minContentWidth,
        localSplitPanelSize,
        localSplitPanelPosition,
        splitPanelSize,
        splitPanelPreferences,
        setSplitPanelSize,
        setLocalSplitPanelSize,
        setLocalSplitPanelPosition
    ]);
    var splitPanelBottomOffset = (splitPanel && splitPanelPosition === 'bottom' && splitPanelHeight) || undefined;
    return (React.createElement("div", __assign({}, baseProps, { className: clsx(styles.root, baseProps.className, disableBodyScroll && styles['root-no-scroll']), ref: __internalRootRef }),
        React.createElement("div", { className: styles['layout-wrapper'], style: (_b = {},
                _b[disableBodyScroll ? 'height' : 'minHeight'] = "calc(100vh - " + headerHeight + "px - " + footerHeight + "px)",
                _b) },
            isMobile && (!toolsHide || !navigationHide || breadcrumbs) && (React.createElement(MobileToolbar, { anyPanelOpen: anyPanelOpen, activeElement: activeElement, topOffset: headerHeight, ariaLabels: ariaLabels, navigationHide: navigationHide, toolsHide: toolsHide, onNavigationOpen: function () { return onNavigationToggle(true); }, onToolsOpen: function () { return onToolsToggle(true); } }, breadcrumbs)),
            React.createElement("div", { className: clsx(styles.layout, disableBodyScroll && styles['layout-no-scroll']) },
                !navigationHide && (React.createElement(Drawer, { type: "navigation", isMobile: isMobile, width: navigationWidth, isOpen: navigationOpen, onToggle: onNavigationToggle, activeElement: activeElement, onClick: isMobile ? onNavigationClick : undefined, contentClassName: styles.navigation, closeClassName: styles['navigation-close'], toggleClassName: styles['navigation-toggle'], topOffset: headerHeight, bottomOffset: footerHeight, ariaLabels: ariaLabels }, navigation)),
                React.createElement("main", { ref: legacyScrollRootRef, className: clsx(styles['layout-main'], (_c = {},
                        _c[styles['layout-main-scrollable']] = disableBodyScroll,
                        _c[styles.unfocusable] = isMobile && anyPanelOpen,
                        _c)) },
                    notifications && (React.createElement(Notifications, { labels: ariaLabels, topOffset: disableBodyScroll ? 0 : headerHeight, sticky: !isMobile && stickyNotifications, ref: notificationsRef }, notifications)),
                    React.createElement("div", { ref: mainPanelRef, style: {
                            marginBottom: splitPanelBottomOffset
                        } },
                        !isMobile && breadcrumbs && (React.createElement("div", { className: clsx(styles.breadcrumbs, styles['breadcrumbs-desktop']) }, breadcrumbs)),
                        React.createElement("div", { className: clsx(disableContentPaddings ? undefined : styles['content-wrapper'], isMobile && !disableContentPaddings && styles['content-wrapper-mobile']), ref: mainContentRef },
                            React.createElement("div", { className: styles.content, style: !isMobile ? { minWidth: defaults.minContentWidth, maxWidth: defaults.maxContentWidth } : undefined },
                                React.createElement(AppLayoutDomContext.RootProvider, { value: {
                                        stickyOffsetTop: headerHeight + (notificationsHeight || 0),
                                        stickyOffsetBottom: footerHeight + (splitPanelBottomOffset || 0)
                                    } }, content)))),
                    splitPanel && isBottomSplitPanel && wrappedSplitPanel),
                splitPanel && isSideSplitPanel && wrappedSplitPanel,
                !toolsHide && (React.createElement(Drawer, { type: "tools", isMobile: isMobile, width: toolsWidth, isOpen: toolsOpen, onToggle: onToolsToggle, activeElement: activeElement, contentClassName: styles.tools, closeClassName: styles['tools-close'], toggleClassName: styles['tools-toggle'], topOffset: headerHeight, bottomOffset: footerHeight, ariaLabels: ariaLabels }, tools))))));
});
applyDisplayName(AppLayout, 'AppLayout');
export default AppLayout;
//# sourceMappingURL=index.js.map